/**
 * Create / edit page for an Owl routing strategy. Reached from the Routing
 * Strategies list via `routing/new?domain=` and `routing/edit?domain=&id=`.
 * The `detail` config is profile-specific, so the form swaps in a structured
 * editor matched by the profile id (latency thresholds / static-route /
 * regional-latency / weighted) and falls back to a raw-JSON editor for profiles
 * whose shape isn't modelled yet (multi_route, …). `profile_id` is immutable,
 * so the profile select is disabled on edit.
 *
 *   1  Static Routing            → static
 *   6  Best latency              → latency
 *   7  Best availability         → latency
 *   8  Time-based Weighted       → weighted (exactly 2 CDNs)
 *   9  Regional latency routing  → regional
 *   10 Request-based Weighted    → weighted (exactly 2 CDNs)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Route as RouteIcon, ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  IconButton,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWafSelectablePlatforms,
  useWafStrategyProfiles,
  useWafStrategies,
  useCreateWafStrategy,
  useUpdateWafStrategy,
  wafExtractError,
  wafStrategyLabel,
  type WafStrategy,
  type WafStrategyProfile,
  type WafGeolocation,
} from '@/api/waf';
import {
  geoCountries,
  geoRegions,
  geoCities,
  geoNetworks,
  countryName,
  regionsOf,
  getGeoCity,
  humanizeGeoName,
} from '../_shared/geo';
import { MultiSelect, type MultiSelectOption } from '../_shared/MultiSelect';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from './WafScopeBar';
import styles from './WafStrategyForm.module.css';

type EditorKind = 'latency' | 'static' | 'regional' | 'weighted' | 'json';

/** Pick the structured editor by profile id (see the file header for the map). */
function editorKind(profileId?: number): EditorKind {
  switch (profileId) {
    case 6: // best_latency
    case 7: // best_availability
      return 'latency';
    case 1: // static_route
      return 'static';
    case 9: // regional_latency
      return 'regional';
    case 8: // time-based weighted
    case 10: // request-based weighted
      return 'weighted';
    default:
      return 'json';
  }
}

/** A stacked label + control form cell. */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      {children}
      {hint ? (
        <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>{hint}</span>
      ) : null}
    </div>
  );
}

const num = (s: string): number | undefined => (s.trim() === '' ? undefined : Number(s));
const str = (v: unknown): string => (v === undefined || v === null ? '' : String(v));

// ---- Geolocation editor --------------------------------------------------
//
// Country / Region / City / Network are four fully independent multi-selects.
// You can start anywhere — pick a network on its own, or a region then its
// cities, without first choosing a country. Each select shows its full catalog
// by default; once a *parent* is chosen the child list narrows to it (the
// cascade: country → its regions, region → its cities, country/city → networks).
//
// Serialisation is the cartesian product grouped by country. Each emitted entry
// carries its full parent chain (the vendor auto-adds ancestors): a selected
// region/city/network always submits its country, and a city also submits its
// parent region — a lone network → `{ country, network }`, a region → `{ country,
// region }`, a city → `{ country, region, city }`. Region/city/network selections
// are stored as `country:code` composite keys so they stay unambiguous across
// countries even when the country itself wasn't explicitly picked.

const splitKey = (k: string): [string, string] => {
  const i = k.indexOf(':');
  return [k.slice(0, i), k.slice(i + 1)];
};

/**
 * Cartesian-expand the four selection sets into `WafGeolocation[]`, grouped by
 * the country each selection belongs to. Every entry carries its parent chain:
 * the country is always emitted for any selection under it, and a city also
 * carries its parent region — so a child selection auto-includes its ancestors.
 */
function buildGeolocations(
  countries: string[],
  regions: string[],
  cities: string[],
  networks: string[],
): WafGeolocation[] {
  // Every country referenced by any selection becomes a grouping bucket.
  const groups = new Set<string>(countries);
  for (const k of [...regions, ...cities, ...networks]) groups.add(splitKey(k)[0]);

  const out: WafGeolocation[] = [];
  for (const country of groups) {
    const nets = networks.filter((k) => splitKey(k)[0] === country).map((k) => splitKey(k)[1]);
    const netDim: (string | undefined)[] = nets.length ? nets : [undefined];

    const cityCodes = cities.filter((k) => splitKey(k)[0] === country).map((k) => splitKey(k)[1]);
    const regionCodes = regions.filter((k) => splitKey(k)[0] === country).map((k) => splitKey(k)[1]);

    // (region, city) pairs — a city pulls in its own parent region.
    const pairs: [string | undefined, string | undefined][] = [];
    if (cityCodes.length) {
      for (const cityCode of cityCodes) {
        const region = getGeoCity(country, cityCode)?.region || undefined;
        pairs.push([region, cityCode]);
      }
      // Regions chosen but with no city under them → region-only entries.
      for (const region of regionCodes) {
        if (!cityCodes.some((cc) => getGeoCity(country, cc)?.region === region)) pairs.push([region, undefined]);
      }
    } else if (regionCodes.length) {
      for (const region of regionCodes) pairs.push([region, undefined]);
    } else {
      pairs.push([undefined, undefined]);
    }

    for (const [region, city] of pairs) {
      for (const network of netDim) {
        const e: WafGeolocation = {};
        // Always carry the parent chain — a selected region/city/network submits
        // its country too (city also carries its region), matching the vendor.
        if (country) e.country = country;
        if (region !== undefined) e.region = region;
        if (city !== undefined) e.city = city;
        if (network !== undefined) e.network = network;
        if (Object.keys(e).length) out.push(e);
      }
    }
  }
  return out;
}

/**
 * Recover the four selection sets from an existing `geolocations` array. The
 * country dimension is only re-selected when an entry actually carries `country`;
 * for child codes that stand alone (e.g. a lone `{ network }`) the parent country
 * is inferred from the catalog so the `country:code` key can be rebuilt.
 */
function parseGeolocations(geos: WafGeolocation[]) {
  const countries = new Set<string>();
  const regions = new Set<string>();
  const cities = new Set<string>();
  const networks = new Set<string>();
  for (const g of geos) {
    if (g.country) countries.add(g.country);
    if (g.region) {
      const c = g.country ?? geoRegions.find((r) => r.code === g.region)?.country;
      if (c) regions.add(`${c}:${g.region}`);
    }
    if (g.city) {
      const c = g.country ?? geoCities.find((ci) => ci.code === g.city)?.country;
      if (c) cities.add(`${c}:${g.city}`);
    }
    if (g.network) {
      const c = g.country ?? geoNetworks.find((n) => n.code === g.network)?.country;
      if (c) networks.add(`${c}:${g.network}`);
    }
  }
  return {
    countries: [...countries],
    regions: [...regions],
    cities: [...cities],
    networks: [...networks],
  };
}

/** The four cascading geolocation multi-selects. */
function GeoMultiEditor({
  countries,
  regions,
  cities,
  networks,
  setCountries,
  setRegions,
  setCities,
  setNetworks,
  disabled,
}: {
  countries: string[];
  regions: string[];
  cities: string[];
  networks: string[];
  setCountries: (v: string[]) => void;
  setRegions: (v: string[]) => void;
  setCities: (v: string[]) => void;
  setNetworks: (v: string[]) => void;
  disabled?: boolean;
}) {
  // Build `{value,label}` options from a catalog slice; prefix the country name
  // when the slice spans more than one country (so codes stay unambiguous).
  const toOpts = (items: { country: string; code: string; name: string }[]): MultiSelectOption[] => {
    const multi = new Set(items.map((i) => i.country)).size > 1;
    return items.map((i) => ({
      value: `${i.country}:${i.code}`,
      label: multi ? `${countryName(i.country)} · ${humanizeGeoName(i.name)}` : humanizeGeoName(i.name),
    }));
  };

  const countryOpts = useMemo<MultiSelectOption[]>(
    () =>
      [...geoCountries]
        .sort((a, b) => countryName(a.code).localeCompare(countryName(b.code)))
        .map((c) => ({ value: c.code, label: countryName(c.code) })),
    [],
  );

  // Region list narrows to the chosen countries; full catalog otherwise.
  const regionOpts = useMemo<MultiSelectOption[]>(
    () => toOpts(countries.length ? countries.flatMap((c) => regionsOf(c)) : geoRegions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countries],
  );

  // City list narrows to chosen regions, else chosen countries, else full catalog.
  const cityOpts = useMemo<MultiSelectOption[]>(() => {
    const list = regions.length
      ? geoCities.filter((c) => regions.includes(`${c.country}:${c.region}`))
      : countries.length
        ? geoCities.filter((c) => countries.includes(c.country))
        : geoCities;
    return toOpts(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, regions]);

  // Network list narrows to chosen cities' countries, else chosen countries, else all.
  const networkOpts = useMemo<MultiSelectOption[]>(() => {
    const cityCountries = new Set(cities.map((k) => splitKey(k)[0]));
    const list = cities.length
      ? geoNetworks.filter((n) => cityCountries.has(n.country))
      : countries.length
        ? geoNetworks.filter((n) => countries.includes(n.country))
        : geoNetworks;
    return toOpts(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, cities]);

  // Prune selections that fall out of their (now narrower) option set.
  useEffect(() => {
    const valid = new Set(regionOpts.map((o) => o.value));
    if (regions.some((v) => !valid.has(v))) setRegions(regions.filter((v) => valid.has(v)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionOpts]);
  useEffect(() => {
    const valid = new Set(cityOpts.map((o) => o.value));
    if (cities.some((v) => !valid.has(v))) setCities(cities.filter((v) => valid.has(v)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityOpts]);
  useEffect(() => {
    const valid = new Set(networkOpts.map((o) => o.value));
    if (networks.some((v) => !valid.has(v))) setNetworks(networks.filter((v) => valid.has(v)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkOpts]);

  // Chain gating — Country → Region → City → Network must stay one contiguous
  // run. You pick a starting level, then may only extend *downward*: levels above
  // the start are locked, and you can't skip a level (region → network needs a
  // city in between). `lo` = topmost selected level; `blockEnd` = last level of
  // the unbroken run below it; the next level down (`blockEnd + 1`) is unlocked.
  const levelSel = [countries, regions, cities, networks];
  const lo = levelSel.findIndex((l) => l.length > 0);
  let blockEnd = lo;
  if (lo >= 0) while (blockEnd + 1 < levelSel.length && levelSel[blockEnd + 1].length > 0) blockEnd++;
  const levelEnabled = (i: number) => lo < 0 || (i >= lo && i <= blockEnd) || i === blockEnd + 1;

  // Drop selections stranded past a gap (e.g. deselecting City orphans Network).
  useEffect(() => {
    if (lo < 0) return;
    if (cities.length && 2 > blockEnd) setCities([]);
    if (networks.length && 3 > blockEnd) setNetworks([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lo, blockEnd]);

  const cell = (label: string, control: React.ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
      {control}
    </div>
  );

  return (
    <Field
      label="Geolocations"
      hint="Scopes this strategy applies to. Each level is independent — pick a network on its own, or a region then its cities. Choosing a parent narrows the lists below it."
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--vn-space-3)' }}>
        {cell('Countries', <MultiSelect placeholder="Any country" options={countryOpts} value={countries} onChange={setCountries} disabled={disabled || !levelEnabled(0)} />)}
        {cell('Regions', <MultiSelect placeholder="Any region" options={regionOpts} value={regions} onChange={setRegions} disabled={disabled || !levelEnabled(1)} />)}
        {cell('Cities', <MultiSelect placeholder="Any city" options={cityOpts} value={cities} onChange={setCities} disabled={disabled || !levelEnabled(2)} />)}
        {cell('Networks', <MultiSelect placeholder="Any network" options={networkOpts} value={networks} onChange={setNetworks} disabled={disabled || !levelEnabled(3)} />)}
      </div>
    </Field>
  );
}

// ---- Form ----------------------------------------------------------------

interface PlatformRow {
  id: string;
  /** regional_latency only. */
  handicap: string;
  /** weighted (8 / 10) only. */
  weight: string;
  /** weighted (8 / 10) only. */
  enforce: boolean;
}

const blankPlatform = (): PlatformRow => ({ id: '', handicap: '', weight: '100', enforce: true });

/** The actual form fields + actions. Mounted only once the data it seeds from is ready. */
function StrategyFormBody({
  serviceId,
  domain,
  strategy,
  profiles,
  onDone,
}: {
  serviceId: string;
  domain: string;
  strategy?: WafStrategy;
  profiles: WafStrategyProfile[];
  onDone: () => void;
}) {
  const { show } = useToast();
  const isEdit = !!strategy;
  // Domain-scoped CDN picker — only platforms toggleable for this subdomain.
  const platformsQ = useWafSelectablePlatforms(serviceId, domain);
  const platforms = platformsQ.data ?? [];
  // Options are the selectable ones; label lookup spans all (so an already-set,
  // now-unselectable platform still shows a name when editing).
  const selectablePlatforms = useMemo(() => platforms.filter((p) => p.selectable), [platforms]);
  const platformLabel = (id: string) =>
    id === '0' ? 'Origin' : platforms.find((p) => p.id === id)?.display_name ?? id;
  // Options for the platform select on row `i` — drop platforms already chosen on
  // other rows so the same CDN can't be picked twice (row i keeps its own value).
  const platformOptionsFor = (i: number) => {
    const taken = new Set(platRows.filter((_, idx) => idx !== i).map((r) => r.id).filter(Boolean));
    return selectablePlatforms.filter((p) => !taken.has(p.id));
  };

  const [profileId, setProfileId] = useState<number>(strategy?.profile_id ?? profiles[0]?.id ?? 0);
  const [name, setName] = useState(strategy?.name ?? '');

  // latency
  const [maxRtt, setMaxRtt] = useState('');
  const [minAvailPct, setMinAvailPct] = useState('');
  const [maxDelay, setMaxDelay] = useState('');
  const [minPoints, setMinPoints] = useState('');
  // static
  const [platform, setPlatform] = useState('');
  // geolocations (shared by static / regional / weighted) — `country:code` keys
  const [geoCountrySel, setGeoCountrySel] = useState<string[]>([]);
  const [geoRegionSel, setGeoRegionSel] = useState<string[]>([]);
  const [geoCitySel, setGeoCitySel] = useState<string[]>([]);
  const [geoNetworkSel, setGeoNetworkSel] = useState<string[]>([]);
  // regional / weighted
  const [platRows, setPlatRows] = useState<PlatformRow[]>([]);
  const [lastCheck, setLastCheck] = useState('');
  const [maxLatency, setMaxLatency] = useState('');
  const [minAvail, setMinAvail] = useState('');
  // json
  const [jsonText, setJsonText] = useState('');

  const kind = editorKind(profileId);

  // Seed the profile-specific editors from `detail` on mount and when the type
  // changes. `name`/`profileId` are seeded by their own `useState` initialisers,
  // so switching type here doesn't wipe a name the user already typed.
  useEffect(() => {
    const d = (strategy?.detail ?? {}) as Record<string, any>;
    // latency
    setMaxRtt(str(d.max_rtt_avg_ms));
    setMinAvailPct(str(d.min_avail_percentage));
    setMaxDelay(str(d.max_updated_delay_seconds));
    setMinPoints(str(d.min_data_points_num));
    // static
    setPlatform(str(d.platform));
    // geolocations → split back into the four selection sets
    const geoSel = parseGeolocations(Array.isArray(d.geolocations) ? (d.geolocations as WafGeolocation[]) : []);
    setGeoCountrySel(geoSel.countries);
    setGeoRegionSel(geoSel.regions);
    setGeoCitySel(geoSel.cities);
    setGeoNetworkSel(geoSel.networks);
    // regional / weighted (both nest under config.platforms)
    const cfg = (d.config ?? {}) as Record<string, any>;
    const rawPlat: PlatformRow[] = Array.isArray(cfg.platforms)
      ? cfg.platforms.map((p: any) => ({
          id: str(p.id),
          handicap: str(p.handicap),
          weight: p.weight === undefined ? '100' : str(p.weight),
          enforce: p.enforce ?? true,
        }))
      : [];
    if (profileId === 8 || profileId === 10) {
      // Weighted profiles are exactly two CDNs — pad/trim to two fixed rows.
      const padded = [...rawPlat];
      while (padded.length < 2) padded.push(blankPlatform());
      setPlatRows(padded.slice(0, 2));
    } else {
      setPlatRows(rawPlat);
    }
    setLastCheck(str(cfg.last_check));
    setMaxLatency(str(cfg.max_latency));
    setMinAvail(str(cfg.min_avail));
    // json
    setJsonText(strategy?.detail ? JSON.stringify(strategy.detail, null, 2) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy, profileId]);

  const create = useCreateWafStrategy(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Strategy created' }); trackFeatureAction('waf', 'routing', 'create'); onDone(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Create failed') }),
  });
  const update = useUpdateWafStrategy(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Strategy updated' }); trackFeatureAction('waf', 'routing', 'edit'); onDone(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const pending = create.isPending || update.isPending;

  let jsonError = '';
  if (kind === 'json' && jsonText.trim()) {
    try { JSON.parse(jsonText); } catch { jsonError = 'Invalid JSON'; }
  }

  const geolocations = useMemo(
    () => buildGeolocations(geoCountrySel, geoRegionSel, geoCitySel, geoNetworkSel),
    [geoCountrySel, geoRegionSel, geoCitySel, geoNetworkSel],
  );
  const weightedIds = platRows.filter((p) => p.id).map((p) => p.id);
  const weightedOk = weightedIds.length === 2 && new Set(weightedIds).size === 2;
  const valid =
    !!name.trim() &&
    !!profileId &&
    (kind !== 'static' || (!!platform && geolocations.length > 0)) &&
    (kind !== 'regional' || platRows.some((p) => p.id)) &&
    (kind !== 'weighted' || weightedOk) &&
    (kind !== 'json' || !jsonError);

  const buildDetail = (): Record<string, unknown> => {
    if (kind === 'latency') {
      const d: Record<string, unknown> = {};
      if (num(maxRtt) !== undefined) d.max_rtt_avg_ms = num(maxRtt);
      if (num(minAvailPct) !== undefined) d.min_avail_percentage = num(minAvailPct);
      if (num(maxDelay) !== undefined) d.max_updated_delay_seconds = num(maxDelay);
      if (num(minPoints) !== undefined) d.min_data_points_num = num(minPoints);
      return d;
    }
    if (kind === 'static') {
      return { platform, geolocations };
    }
    if (kind === 'regional') {
      const config: Record<string, unknown> = {
        platforms: platRows.filter((p) => p.id).map((p) => ({ id: p.id, handicap: num(p.handicap) ?? 0 })),
      };
      if (num(lastCheck) !== undefined) config.last_check = num(lastCheck);
      if (num(maxLatency) !== undefined) config.max_latency = num(maxLatency);
      if (num(minAvail) !== undefined) config.min_avail = num(minAvail);
      return { config, geolocations };
    }
    if (kind === 'weighted') {
      const config: Record<string, unknown> = {
        platforms: platRows
          .filter((p) => p.id)
          .map((p) => ({ id: p.id, weight: num(p.weight) ?? 0, enforce: p.enforce })),
      };
      if (num(lastCheck) !== undefined) config.last_check = num(lastCheck);
      if (num(maxLatency) !== undefined) config.max_latency = num(maxLatency);
      if (num(minAvail) !== undefined) config.min_avail = num(minAvail);
      return { config, geolocations };
    }
    return jsonText.trim() ? (JSON.parse(jsonText) as Record<string, unknown>) : {};
  };

  const submit = () => {
    if (!valid) return;
    const detail = buildDetail();
    if (isEdit && strategy) update.mutate({ domain, id: strategy.id, input: { name: name.trim(), detail } });
    else create.mutate({ domain, input: { profile_id: profileId, name: name.trim(), detail } });
  };

  const numField = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    placeholder?: string,
  ) => (
    <Field label={label}>
      <Input
        type="number"
        className={styles.noSpin}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={pending}
      />
    </Field>
  );

  return (
    <>
      <div className={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
          <Field label="Type" hint={isEdit ? 'Type cannot be changed after creation.' : undefined}>
            <Select
              value={String(profileId)}
              onValueChange={(v) => setProfileId(Number(v))}
              disabled={pending || isEdit}
            >
              <SelectTrigger aria-label="Strategy type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{wafStrategyLabel(p.id, p.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Name">
            <Input placeholder="my-strategy" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} />
          </Field>
        </div>

        {kind === 'latency' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            {numField('Max avg RTT (ms)', maxRtt, setMaxRtt, '3000')}
            {numField('Min availability (%)', minAvailPct, setMinAvailPct, '90')}
            {numField('Max data delay (s)', maxDelay, setMaxDelay, '600')}
            {numField('Min data points', minPoints, setMinPoints, '3')}
          </div>
        )}

        {kind === 'static' && (
          <>
            <Field label="Platform">
              <Select value={platform} onValueChange={setPlatform} disabled={pending || platformsQ.isLoading}>
                <SelectTrigger aria-label="Platform">
                  <SelectValue placeholder="Select a CDN platform" />
                </SelectTrigger>
                <SelectContent>
                  {selectablePlatforms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{platformLabel(p.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <GeoMultiEditor
              countries={geoCountrySel}
              regions={geoRegionSel}
              cities={geoCitySel}
              networks={geoNetworkSel}
              setCountries={setGeoCountrySel}
              setRegions={setGeoRegionSel}
              setCities={setGeoCitySel}
              setNetworks={setGeoNetworkSel}
              disabled={pending}
            />
          </>
        )}

        {kind === 'regional' && (
          <>
            <Field label="Platforms" hint="Each platform with its latency handicap (ms).">
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {platRows.map((row, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    <Select
                      value={row.id}
                      onValueChange={(v) => setPlatRows(platRows.map((r, idx) => (idx === i ? { ...r, id: v } : r)))}
                      disabled={pending || platformsQ.isLoading}
                    >
                      <SelectTrigger aria-label="Platform" style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <SelectValue placeholder="Platform" />
                      </SelectTrigger>
                      <SelectContent>
                        {platformOptionsFor(i).map((p) => (
                          <SelectItem key={p.id} value={p.id}>{platformLabel(p.id)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      className={styles.noSpin}
                      style={{ flex: '0 0 110px', width: 110 }}
                      placeholder="Handicap"
                      value={row.handicap}
                      onChange={(e) => setPlatRows(platRows.map((r, idx) => (idx === i ? { ...r, handicap: e.target.value } : r)))}
                      disabled={pending}
                    />
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label="Remove platform"
                      onClick={() => setPlatRows(platRows.filter((_, idx) => idx !== i))}
                      disabled={pending}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                ))}
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    startIcon={<Plus size={14} />}
                    onClick={() => setPlatRows([...platRows, blankPlatform()])}
                    disabled={pending}
                  >
                    Add platform
                  </Button>
                </div>
              </div>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
              {numField('Last check (s)', lastCheck, setLastCheck, '600')}
              {numField('Max latency (ms)', maxLatency, setMaxLatency, '3000')}
              {numField('Min availability (%)', minAvail, setMinAvail, '90')}
            </div>
            <GeoMultiEditor
              countries={geoCountrySel}
              regions={geoRegionSel}
              cities={geoCitySel}
              networks={geoNetworkSel}
              setCountries={setGeoCountrySel}
              setRegions={setGeoRegionSel}
              setCities={setGeoCitySel}
              setNetworks={setGeoNetworkSel}
              disabled={pending}
            />
          </>
        )}

        {kind === 'weighted' && (
          <>
            <Field
              label="CDN platforms"
              hint="Exactly two CDNs. Weight sets the traffic split; Enforce keeps a CDN in rotation even when it's degraded."
            >
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {platRows.map((row, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    <Select
                      value={row.id}
                      onValueChange={(v) => setPlatRows(platRows.map((r, idx) => (idx === i ? { ...r, id: v } : r)))}
                      disabled={pending || platformsQ.isLoading}
                    >
                      <SelectTrigger aria-label={`Platform ${i + 1}`} style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <SelectValue placeholder="Platform" />
                      </SelectTrigger>
                      <SelectContent>
                        {platformOptionsFor(i).map((p) => (
                          <SelectItem key={p.id} value={p.id}>{platformLabel(p.id)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      className={styles.noSpin}
                      style={{ flex: '0 0 100px', width: 100 }}
                      placeholder="Weight"
                      value={row.weight}
                      onChange={(e) => setPlatRows(platRows.map((r, idx) => (idx === i ? { ...r, weight: e.target.value } : r)))}
                      disabled={pending}
                    />
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap' }}>
                      <Switch
                        checked={row.enforce}
                        aria-label="Enforce"
                        onCheckedChange={(v) => setPlatRows(platRows.map((r, idx) => (idx === i ? { ...r, enforce: v } : r)))}
                        disabled={pending}
                      />
                      Enforce
                    </label>
                  </div>
                ))}
              </div>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--vn-space-3)' }}>
              {numField('Last check (s)', lastCheck, setLastCheck, '600')}
              {numField('Max latency (ms)', maxLatency, setMaxLatency, '3000')}
              {numField('Min availability (%)', minAvail, setMinAvail, '90')}
            </div>
            <GeoMultiEditor
              countries={geoCountrySel}
              regions={geoRegionSel}
              cities={geoCitySel}
              networks={geoNetworkSel}
              setCountries={setGeoCountrySel}
              setRegions={setGeoRegionSel}
              setCities={setGeoCitySel}
              setNetworks={setGeoNetworkSel}
              disabled={pending}
            />
          </>
        )}

        {kind === 'json' && (
          <Field
            label="Detail (JSON)"
            hint="This strategy type isn't modelled yet — edit its config as raw JSON."
          >
            <Textarea
              rows={10}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              disabled={pending}
              invalid={!!jsonError}
              placeholder="{}"
              style={{ fontFamily: 'var(--vn-font-mono)' }}
            />
            {jsonError ? (
              <span style={{ color: 'var(--vn-danger-text, var(--vn-color-red-500))', fontSize: 'var(--vn-font-size-sm)' }}>
                {jsonError}
              </span>
            ) : null}
          </Field>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onDone} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={pending || !valid}>
          {isEdit ? 'Save changes' : 'Create strategy'}
        </Button>
      </div>
    </>
  );
}

// ---- Page ----------------------------------------------------------------

export function WafStrategyForm({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { serviceId: paramId } = useParams<{ serviceId: string }>();
  const sid = serviceId || paramId || '';
  const [params, setParams] = useSearchParams();
  const urlDomain = params.get('domain') ?? '';
  const idParam = params.get('id');
  const isEdit = !!idParam;

  // On the create page the scope bar can switch subdomain; in edit mode the
  // strategy is bound to its original subdomain, so the bar is hidden and the
  // domain stays whatever the URL carries.
  const [scope, setScope] = useState<WafScope>({});
  const domain = (!isEdit && scope.subdomain) || urlDomain;

  // Keep the URL's `domain` in sync with the scope bar so reload / back work.
  useEffect(() => {
    if (isEdit) return;
    const sub = scope.subdomain;
    if (sub && sub !== urlDomain) {
      const next = new URLSearchParams(params);
      next.set('domain', sub);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.subdomain]);

  const back = () =>
    navigate(`/services/waf/${sid}/routing${domain ? `?domain=${encodeURIComponent(domain)}` : ''}`);

  const profilesQ = useWafStrategyProfiles(sid);
  // Strategies are only needed to resolve the one being edited.
  const strategiesQ = useWafStrategies(sid, domain, isEdit);
  const profiles = profilesQ.data ?? [];
  const strategy = isEdit ? strategiesQ.data?.find((s) => String(s.id) === idParam) : undefined;

  const loading = profilesQ.isPending || (isEdit && strategiesQ.isPending);

  return (
    <Container size="md">
      <button type="button" className={styles.backBtn} onClick={back}>
        <ArrowLeft size={14} /> Routing Strategies
      </button>
      <PageHeader
        icon={<RouteIcon size={22} />}
        title={isEdit ? `Edit strategy${strategy ? ` — ${strategy.name}` : ''}` : 'New routing strategy'}
        description={`Decide how ${domain || 'this subdomain'}'s traffic is routed across CDN platforms.`}
      />

      {!isEdit && (
        <div style={{ marginBottom: 'var(--vn-space-4)' }}>
          <WafScopeBar serviceId={sid} mode="subdomain" onChange={setScope} />
        </div>
      )}

      {!domain ? (
        <EmptyState
          icon={<RouteIcon size={20} />}
          title="Select a subdomain"
          description="Pick a subdomain above to add a routing strategy for it."
        />
      ) : loading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 56 }} />
          ))}
        </div>
      ) : isEdit && !strategy ? (
        <EmptyState
          icon={<RouteIcon size={20} />}
          title="Strategy not found"
          description="This routing strategy no longer exists — it may have been deleted."
          actions={<Button onClick={back}>Back to Routing Strategies</Button>}
        />
      ) : (
        <StrategyFormBody
          key={`${domain}:${idParam ?? 'new'}`}
          serviceId={sid}
          domain={domain}
          strategy={strategy}
          profiles={profiles}
          onDone={back}
        />
      )}
    </Container>
  );
}

export default WafStrategyForm;
